// src/theme/strings.ts
// Every user-facing string, grouped by screen. Screens render registry
// keys only — copy edits and future localization happen here.
export const strings = {
  onboarding: {
    intentTitle: "What do you want to build?",
    intentSubtitle: "Pick a path — you can switch anytime.",
    buildCardTitle: "Build an app",
    buildCardSub: "Describe it and we'll create a starter you can preview.",
    // No "Try example apps" card: it led to demos in My Apps, and My Apps is now the person's
    // own live apps. A real example is one link away instead, with no account needed.
    peekLink: "See a real one first →",
    peekLinkLabel: "Open a real example app in your browser",
    personalCardTitle: "Just for me",
    personalCardSub: "A private app only you use — your data stays yours.",
    sharableCardTitle: "For others",
    sharableCardSub: "Customers or a team can use it; you can publish it.",
    shareFeatureTitle: "Let others sign in & use it",
    shareFeatureSub: "Adds user accounts, roles, and lets you publish it.",
    signInPrompt: "Already have an account?",
    signInAction: "Sign in",
    typeTitle: "What kind of app?",
    typeNext: "Next",
    describeTitle: "What should it do?",
    describePlaceholder: "e.g. track my workouts with sets, reps and progress",
    describeNext: "Next",
    nameTitle: "Name your app",
    namePlaceholder: "My App",
    nameNext: "Continue",
  },
  paywall: {
    title: "Unlock more",
    // Shown only when the balance really is empty. Not "you've used your free credits": since
    // 2026-09-29 a new account has none to use, and the first build is where it meets this.
    subtitle:
      "Building and changing apps needs a subscription.",
    // The common case since charging moved BEFORE the work: there are credits left, just
    // not enough for this action. Saying "you've used your credits" while the header
    // behind the sheet shows a positive number is a small lie in the place that asks for
    // money.
    //
    // A template rather than a function: every leaf of this registry has to be a non-empty
    // string, and `registries.test.ts` is right to enforce that — a function here would
    // make "is this copy missing?" unanswerable for the whole tree.
    subtitleShort:
      "This costs {needed} credits and you have {remaining}. Subscribe to keep editing and building.",
    price: "$20",
    per: "/month · 20,000 credits",
    cta: "Subscribe",
    processing: "Processing…",
    restore: "Restore purchase",
    disclosure:
      "Auto-renews monthly until cancelled. Payment is charged to your App Store account at confirmation; manage or cancel anytime in Settings at least 24 hours before renewal.",
    // The App Store wording is wrong in a browser — there is no App Store account and
    // no Settings screen to cancel in.
    disclosureWeb:
      "Auto-renews monthly until cancelled. Payment is charged to your card securely by Stripe; cancel anytime from your account.",
    /*
     * The free trial (the owner's offer, 2026-09-29: seven days free with the card taken up
     * front, then the monthly price). Templates, filled by `subscribeTerms` in paywallOffer.ts
     * from what the STORE says: {length} is the trial the store configured and {price} the
     * price it returned, never a constant, because the constant is only true in one storefront.
     *
     * The disclosure is Guideline 3.1.2's: what happens when the trial ends, what it then costs,
     * how often, and how to stop it — said before the button, not after.
     */
    trialHeadline: "{length} free, then {price}/month",
    trialCta: "Start free trial",
    trialPer: "20,000 credits a month, starting today",
    trialDisclosure:
      "Your free trial ends after {length} and your subscription starts automatically unless you cancel at least 24 hours before the trial ends. {price} is then charged to your App Store account and it renews every month until cancelled. Manage or cancel anytime in Settings.",
    // Stripe charges when the trial ends unless it was cancelled first: no App Store account,
    // no Settings screen and no 24-hour rule.
    trialDisclosureWeb:
      "Your free trial ends after {length} and your subscription starts automatically unless you cancel before then. {price} is then charged to your card securely by Stripe and it renews every month until cancelled. Cancel anytime from your account.",
    // "Started", not "paid": nothing has been charged yet, and the credits come from the webhook.
    trialSuccess: "Your free trial has started! Your credits will show up in a moment.",
    signInRequired:
      "Sign in first so your subscription lands on the right account.",
    terms: "Terms of Use",
    privacy: "Privacy Policy",
    dismiss: "Not now",
    // Not "in this build" — a person reading that does not know what a build is, and it
    // sounds like the product is broken rather than the App Store being briefly unreachable.
    unavailable: "Subscriptions aren’t available on this device right now. Try again in a moment.",
    // A top-up that cannot be bought is not a subscription that cannot be bought.
    topupUnavailable: "Credits can’t be bought on this device right now. Try again in a moment.",
    // "Will show up", not "have been added": the server grants them when the store's webhook
    // lands, which can be a few seconds after this is shown.
    success: "You're subscribed! Your credits will show up in a moment.",
    failed: "Purchase failed. Please try again.",
    nothingToRestore: "No previous purchase found to restore.",
    /*
     * A SUBSCRIBER whose month has run out. They were shown "Subscribe" — a plan they already
     * pay for, and on the web a second one they could buy. The amounts are written out and
     * pinned to the server's table by paywallOffer.test.ts.
     */
    titlePaid: "More credits",
    subtitlePaid: "You've used this month's credits.",
    subtitleShortPaid: "This costs {needed} credits and you have {remaining}.",
    topupCta: "Buy 10,000 credits",
    topupPer: "one time · added to your balance",
    // Bought credits are kept at renewal (App Review 3.1.1; usage.ts `creditsPurchased`).
    topupDisclosure:
      "A one-time purchase. Bought credits don't expire and are kept when your plan renews.",
    topupSuccess: "Done — 10,000 credits are on their way to your account.",
    // No top-up on sale yet: the honest answer, rather than a button that cannot work.
    refills: "Your 20,000 credits refill when your plan renews.",
    // Ask to Buy, or any purchase the store has deferred: not paid for yet, and not failed.
    pending:
      "Your purchase is waiting for approval. Your credits will be added once it goes through.",
  },
  apps: {
    // My Apps' own words (its empty state included) are MY_APPS_COPY in src/utils/myApps.ts.
    get: "Get",
    open: "Open",
  },
  // No Discover: it is switched off for now, and a tab name with nothing behind it is a
  // sentence a screen could still show.
  tabs: {
    build: "Build",
    myApps: "My Apps",
  },
  account: {
    a11yAvatar: "Account",
  },
} as const;
